import {queries} from '@/shared/integrations/zero'
import {
  ProductionDetailStep,
  type DistributedBatches,
  type DurationUnit,
  type ProductionWeighingStep,
} from '@cypro/engine/database/schema'
import {isDefined, returnProvidedDose, type CompoundingResult, endOfExpiryDay} from '@cypro/engine/utils'
import type {QueryResultType} from '@rocicorp/zero'
import type {LabelSettings, LabelStabilityRule, PreparationLabelInput} from './types'

export type LabelSource = NonNullable<QueryResultType<typeof queries.patientsTherapies.getPreparationLabelSource>>

export interface StatusLog {
  performedAt: number
  details: Record<string, string | null | undefined> | null
  actor:
    | {title?: string | null; firstName?: string | null; lastName?: string | null; name?: string | null}
    | null
    | undefined
}

export interface LabelVolumes {
  totalVolumeMl: number | null
  substanceVolumeMl: number | null
}

export function getTargetVolumes(targets: CompoundingResult | null): LabelVolumes | null {
  if (!targets) return null
  return {
    totalVolumeMl: targets.finalVolumeMl ?? null,
    substanceVolumeMl: targets.substances.reduce((sum, substance) => sum + substance.drugVolumeMl, 0),
  }
}

interface StabilityRuleRow {
  id: string
  stabilityStage: string
  status?: string | null
  carrierDrugId?: string | null
  durationValue: number
  durationUnit: DurationUnit
  lightProtectionRequired?: boolean | null
  storageLabel?: {name: string} | null
}

export function getSequenceBatchIds(distributed: DistributedBatches | null | undefined, sequenceId: string) {
  const item = distributed?.orders?.find(order => order.sequenceId === sequenceId)
  return {
    vial: [
      ...(item?.activeSubstances?.batches.map(b => b.batchId) ?? []),
      ...(item?.restVials?.map(rv => rv.batchId).filter((id): id is string => Boolean(id)) ?? []),
    ],
    carrier: item?.carrier?.batches.map(b => b.batchId) ?? [],
    solvent: item?.solvents?.batches.map(b => b.batchId) ?? [],
  }
}

export function getCompletionLog(logs: readonly StatusLog[]) {
  const entry = logs.find(log => log.details?.action === 'COMPLETE_PRODUCTION')
  if (!entry) return {producedAt: null, preparedByInitials: null}

  const initials = [entry.actor?.firstName, entry.actor?.lastName]
    .map(part => part?.trim()[0])
    .filter(Boolean)
    .join('')
    .toUpperCase()
  return {producedAt: entry.performedAt, preparedByInitials: initials || null}
}

function formatActorName(actor: StatusLog['actor']) {
  if (!actor) return null
  return [actor.title, actor.firstName, actor.lastName].filter(Boolean).join(' ') || actor.name || null
}

/** newest status change for an event action (COMPLETE_PRODUCTION, RELEASE_FOR_DELIVERY, …) */
export function getStatusLogEntry(logs: readonly StatusLog[], action: string) {
  const entry = logs.find(log => log.details?.action === action)
  return entry
    ? {
        performedAt: entry.performedAt,
        actorName: formatActorName(entry.actor),
        // Auto-release for delivery logs the release as automatic (CY-263)
        automatic: entry.details?.automatic === 'true',
      }
    : null
}

type ProductionDetailRow = NonNullable<LabelSource['productionDetails']>[number]
type WeighingDetailRow = ProductionDetailRow & {step: ProductionWeighingStep; actualWeightGrams: number}

/** scale readings only: the PREPARATION_START marker carries no weight */
export function getWeighingDetails(rows: readonly ProductionDetailRow[] | undefined): WeighingDetailRow[] {
  return (rows ?? []).filter(
    (row): row is WeighingDetailRow =>
      row.step !== ProductionDetailStep.enum.PREPARATION_START && isDefined(row.actualWeightGrams)
  )
}

/** when the cleanroom material check was completed and preparation began */
export function getPreparationStart(rows: readonly ProductionDetailRow[] | undefined) {
  return rows?.find(row => row.step === ProductionDetailStep.enum.PREPARATION_START)?.recordedAt ?? null
}

type NullableSettings = {[K in keyof LabelSettings]?: LabelSettings[K] | null}

/** Zero types defaulted columns as nullable; fall back to the database defaults */
export function toLabelSettings(settings: NullableSettings): LabelSettings {
  return {
    language: settings.language ?? 'EN',
    patientNameOrder: settings.patientNameOrder ?? 'LAST_NAME_FIRST',
    prescribingDoctor: settings.prescribingDoctor ?? true,
    hospitalNumber: settings.hospitalNumber ?? true,
    customerName: settings.customerName ?? true,
    wardNextToCustomerName: settings.wardNextToCustomerName ?? false,
    manufacturer: settings.manufacturer ?? true,
    tradeNameBeforeSubstance: settings.tradeNameBeforeSubstance ?? false,
    concentration: settings.concentration ?? true,
    concentrationBasis: settings.concentrationBasis ?? 'CARRIER_VOLUME',
    actualQuantity: settings.actualQuantity ?? true,
    preparedBy: settings.preparedBy ?? true,
    pharmacyNameAndAddress: settings.pharmacyNameAndAddress ?? true,
    labelVolume: settings.labelVolume ?? 'TOTAL_VOLUME',
    bandTextCytotoxic: settings.bandTextCytotoxic ?? 'CYTOTOXIC',
    bandTextNonCytotoxic: settings.bandTextNonCytotoxic ?? 'NON-CYTOTOXIC',
    labelCopies: settings.labelCopies ?? 1,
    additionalLabelBarcode: settings.additionalLabelBarcode ?? false,
    labelSize: settings.labelSize ?? 'SIZE_100X90',
  }
}

/**
 * The trade name used for a medicine comes from the picked vial batches. Without a
 * prescribed-name match, only a single-substance preparation may fall back to its
 * one trade name; otherwise the label would show another substance's product.
 */
export function pickTradeName<T extends {prescribedNameId: string | null}>(
  tradeNames: readonly T[],
  prescribedNameId: string | null | undefined,
  substanceCount: number
): T | null {
  const match = prescribedNameId ? tradeNames.find(tn => tn.prescribedNameId === prescribedNameId) : undefined
  if (match) return match
  return substanceCount === 1 ? (tradeNames[0] ?? null) : null
}

function toLabelStabilityRule(rule: StabilityRuleRow): LabelStabilityRule {
  return {
    id: rule.id,
    stabilityStage: rule.stabilityStage,
    status: rule.status ?? null,
    carrierDrugId: rule.carrierDrugId ?? null,
    durationValue: rule.durationValue,
    durationUnit: rule.durationUnit,
    lightProtectionRequired: rule.lightProtectionRequired ?? null,
    storageLabelName: rule.storageLabel?.name ?? null,
  }
}

export function mapLabelSource({
  source,
  statusLogs,
  settings,
  volumes,
}: {
  source: LabelSource
  statusLogs: readonly StatusLog[]
  settings: LabelSettings
  volumes: LabelVolumes | null
}): PreparationLabelInput {
  const batchIds = getSequenceBatchIds(source.order?.distributedOrders, source.id)
  const batchesById = new Map(
    (source.batches ?? []).flatMap(orderBatch => (orderBatch.batch ? [[orderBatch.batch.id, orderBatch.batch]] : []))
  )
  const vialBatches = batchIds.vial.map(id => batchesById.get(id)).filter(isDefined)
  const carrierBatches = batchIds.carrier.map(id => batchesById.get(id)).filter(isDefined)
  // the trade names actually used in production come from the picked vial batches
  const tradeNames = [
    ...new Map(vialBatches.flatMap(batch => (batch.tradeName ? [[batch.tradeName.id, batch.tradeName]] : []))).values(),
  ]
  const carrierTradeNames = [
    ...new Map(
      carrierBatches.flatMap(batch => (batch.tradeName ? [[batch.tradeName.id, batch.tradeName]] : []))
    ).values(),
  ]
  // a return used as the starting material carries no batch/trade name, so its own expiry is the
  // only stability window available for the preparation
  const distributedOrder = source.order?.distributedOrders?.orders?.find(order => order.sequenceId === source.id)
  const returns = distributedOrder?.returns ?? []
  const returnExpiries = returns.flatMap(ret => (ret.expiryDate ? [new Date(ret.expiryDate).getTime()] : []))

  const encounter = source.patient?.encounters?.[0]
  const pharmacy = source.order?.productionLocation?.pharmacy
  const address = pharmacy?.address
  const responsibleDoctor = source.patient?.responsibleDoctor
  const {producedAt, preparedByInitials} = getCompletionLog(statusLogs)

  return {
    settings,
    patient: {
      id: source.patientId,
      familyName: source.patient?.familyName ?? '',
      givenName: source.patient?.givenName ?? '',
      birthDate: source.patient?.birthDate ?? null,
      hospitalPatientId: source.patient?.hospitalPatientId ?? null,
    },
    encounter: encounter
      ? {
          wardName: encounter.ward?.name ?? null,
          doctorName: responsibleDoctor
            ? [responsibleDoctor.title, responsibleDoctor.firstName, responsibleDoctor.lastName]
                .filter(Boolean)
                .join(' ') || responsibleDoctor.name
            : null,
          customerLabelName: encounter.customer ? encounter.customer.labelName || encounter.customer.name : null,
        }
      : null,
    substances: (source.medicine ?? []).map(medicine => {
      const details = medicine.activeSubstance?.activeSubstanceDetails
      const tradeName = pickTradeName(tradeNames, medicine.prescribedNameId, source.medicine?.length ?? 0)
      return {
        medicineId: medicine.id,
        name: medicine.activeSubstance?.name ?? '',
        prescribedDose: medicine.totalAmount ?? 0,
        providedByReturn: returnProvidedDose(returns, medicine, source.medicine?.length ?? 0),
        doseUnit:
          details?.doseUnit?.ucumCode ??
          medicine.activeSubstance?.supportiveMedicationDetails?.doseUnit?.ucumCode ??
          null,
        // the prescription trade name ("Kadcyla"), never the vial's own name with its strength ("Kadcyla 160 mg");
        // a line prescribed by active substance takes the one linked to the vials used
        tradeName: medicine.prescribedName?.name ?? tradeName?.prescribedName?.name ?? null,
        manufacturer: tradeName?.manufacturer?.name ?? null,
        // an unflagged substance keeps the cytotoxic band (safe default)
        cytotoxic: details?.cytotoxic ?? true,
        routeWarning: Boolean(details?.routeWarning),
        containsAlcohol: Boolean(tradeName?.containsAlcohol),
        hazardClasses: (details?.hazardClasses ?? []).map(link => link.hazardClass?.name).filter(isDefined),
      }
    }),
    carrier: source.carrierSolution
      ? {name: source.carrierSolution.name, volumeMl: source.volume ?? null, volumeMode: source.volumeMode ?? null}
      : null,
    routeName: source.applicationForm?.name ?? null,
    durationMinutes: source.duration ?? null,
    totalVolumeMl: volumes?.totalVolumeMl ?? null,
    substanceVolumeMl: volumes?.substanceVolumeMl ?? null,
    administrationDate: source.therapyDate,
    weighings: getWeighingDetails(source.productionDetails).map(row => ({
      step: row.step,
      substanceIndex: row.substanceIndex,
      medicineId: row.medicineId ?? null,
      allocatedDose: row.allocatedDose ?? null,
      expectedWeightGrams: row.expectedWeightGrams ?? null,
      actualWeightGrams: row.actualWeightGrams,
      recordedAt: row.recordedAt ?? null,
    })),
    stabilityRules: tradeNames.flatMap(tradeName =>
      (tradeName.stabilityRules ?? []).map(link => link.stabilityRule).filter(isDefined).map(toLabelStabilityRule)
    ),
    carrierStabilityRules: carrierTradeNames.flatMap(tradeName =>
      (tradeName.stabilityRules ?? []).map(link => link.stabilityRule).filter(isDefined).map(toLabelStabilityRule)
    ),
    // a vial is usable through its expiry day (CY-371)
    drugBatchExpiries: vialBatches.map(batch => batch.expiryDate).filter(isDefined).map(endOfExpiryDay),
    carrierBatchExpiries: carrierBatches.map(batch => batch.expiryDate).filter(isDefined).map(endOfExpiryDay),
    returnExpiries,
    drugIsDrySubstance: tradeNames.some(tradeName => tradeName.medicationProfile?.isDrySubstance),
    carrierIsDrySubstance: carrierTradeNames.some(tradeName => tradeName.medicationProfile?.isDrySubstance),
    carrierDrugId: source.carrierSolutionId ?? null,
    // The prescription number (PR-…) identifies a single bag; the picking-list number is shared by every
    // prescription started together.
    productionNumber: source.internalId ?? '',
    producedAt,
    preparedByInitials,
    vialBatchLots: vialBatches.map(batch => batch.lotNumber).filter(isDefined),
    carrierBatchLots: carrierBatches.map(batch => batch.lotNumber).filter(isDefined),
    pharmacy: pharmacy
      ? {
          name: pharmacy.labelName || pharmacy.name,
          address: address
            ? [address.addressLine, [address.postalCode, address.city].filter(Boolean).join(' ')]
                .filter(Boolean)
                .join(', ') || null
            : null,
        }
      : null,
  }
}
